import { Id, MediaFile, Member, MemberAlias, MembersData } from '@nextforce/api'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useCreateDirectConversation } from 'chat/conversations/queries/create_direct'
import { useDeleteConversation } from 'chat/conversations/queries/delete'
import { useUpdateConversation } from 'chat/conversations/queries/update'
import { useMembers, useMembersWithAlias } from 'chat/members/queries/list'
import {
  useUpdateMemberAdminStatus,
  useUpdateMembers,
} from 'chat/members/queries/update'
import { getAliasDisplayName } from 'chat/members/views/member_alias'
import { useFileFolderActions } from 'file_folder_repository/context'
import { useChatGroupRecentFiles } from 'file_folder_repository/queries/recent'
import { ConversationDetailsScreenProps } from 'home/stack/types'
import React, { useCallback, useMemo } from 'react'
import { confirmationAlert } from 'shared/alerts'
import { StatusBoundary } from 'shared/components/status_boundary'
import { useUser } from 'users/queries/detail'
import { useConversation } from '../queries/detail'
import { useLeaveConversation } from '../queries/leave_group'
import ConversationDetailsView from './view'

const RECENT_DOCUMENT_LIMIT = 10

export function ConversationDetailsScreen({
  navigation,
  route,
}: ConversationDetailsScreenProps) {
  const conversationId = route.params.conversationId
  const { data: user } = useUser()
  const { handleNavigation } = useRoutingContext()
  const { onLongPressResource, onPressFile } = useFileFolderActions()

  const conversationQuery = useConversation(conversationId)
  const conversation = conversationQuery?.data

  const updateConversationMutation = useUpdateConversation(conversationId)
  const deleteConversationMutation = useDeleteConversation(conversationId)
  const leaveConversationMutation = useLeaveConversation(conversationId)
  const createDirectConversationMutation = useCreateDirectConversation()
  const updateMembersMutation = useUpdateMembers()
  const updateMemberAdminStatusMutation = useUpdateMemberAdminStatus()
  const recentFilesQuery = useChatGroupRecentFiles(
    conversationId,
    RECENT_DOCUMENT_LIMIT,
    conversation?.showRecentFiles
  )

  const shouldLoadMembers = !conversation?.hideMemberList

  const membersQuery = useMembersWithAlias(conversationId, shouldLoadMembers)
  const allMembersQuery = useMembers(conversationId, shouldLoadMembers)

  const members = useMemo(() => {
    let conversationMembers: MembersData = []

    if (membersQuery.data) {
      conversationMembers = membersQuery.data
      const userIndex = conversationMembers?.findIndex(
        member => member.id === user?.id
      )
      conversationMembers[userIndex].isAdmin = conversation?.allowEdit
    }

    return conversationMembers
  }, [conversation?.allowEdit, membersQuery.data, user?.id])

  const nonAliasMembers = useMemo(() => {
    return members?.filter(m => !m.isAlias)
  }, [members])

  const memberAliases: MemberAlias[] | undefined = useMemo(() => {
    return members?.filter(m => m.isAlias).map(m => m.firstName as MemberAlias)
  }, [members])

  const onSeeAllFiles = useCallback(() => {
    handleNavigation({
      routeName: 'file_folders_bottom_tab',
      routeParams: {
        screen: 'files',
        params: { conversationId },
        conversationId: conversationId,
      },
    })
  }, [conversationId, handleNavigation])

  const onAddMembers = useCallback(() => {
    handleNavigation({
      routeName: 'add_members',
      routeParams: {
        conversationId,
        companyId: String(conversation?.entityId),
      },
    })
  }, [conversation?.entityId, conversationId, handleNavigation])

  const onAliasRemoved = useCallback(
    async (alias: MemberAlias) => {
      if (!conversation || !memberAliases) {
        return
      }
      const confirmation = await confirmationAlert(
        t('removeAliasConfirmationTitle', {
          alias: getAliasDisplayName(alias),
        }),
        t('removeAliasConfirmationDescription', {
          alias: getAliasDisplayName(alias),
        })
      )
      if (confirmation) {
        const updatedMemberAliases = memberAliases.filter(m => m !== alias)
        await updateMembersMutation.mutateAsync({
          conversationId,
          members: [...nonAliasMembers],
          memberAliases: updatedMemberAliases,
        })
      }
    },
    [
      conversation,
      conversationId,
      memberAliases,
      nonAliasMembers,
      updateMembersMutation,
    ]
  )

  const onRemoveMember = useCallback(
    async (member: Member) => {
      if (!nonAliasMembers) {
        return
      }

      const updatedMembers = nonAliasMembers.filter(
        m => m.id !== member.id && m.id !== user?.id
      )
      await updateMembersMutation.mutateAsync({
        conversationId,
        members: updatedMembers,
        memberAliases: memberAliases,
      })
    },
    [
      conversationId,
      memberAliases,
      nonAliasMembers,
      updateMembersMutation,
      user?.id,
    ]
  )

  const onSendDirectMessage = useCallback(
    async (recipientId: Id) => {
      try {
        const directConversation =
          await createDirectConversationMutation.mutateAsync(recipientId)
        navigation.push('messages', {
          id: directConversation.id,
        })
      } catch (err: any) {
        // FIXME: better send direct error message
        alert(err.message)
      }
    },
    [createDirectConversationMutation, navigation]
  )

  const onToggleAdminStatus = useCallback(
    async (member: Member) => {
      if (!members) {
        return
      }

      const memberIndex = members.findIndex(
        m => m.id === member.id && m.id !== user?.id
      )

      if (memberIndex < 0) {
        return
      }

      await updateMemberAdminStatusMutation.mutateAsync({
        conversationId,
        memberId: member.id,
        adminFlag: !member.isAdmin,
      })
    },
    [conversationId, members, updateMemberAdminStatusMutation, user?.id]
  )

  const onEditName = useCallback(async () => {
    handleNavigation({
      routeName: 'edit_conversation_property',
      routeParams: {
        conversationId,
        key: 'name',
      },
    })
  }, [conversationId, handleNavigation])

  const onEditDescription = useCallback(async () => {
    handleNavigation({
      routeName: 'edit_conversation_property',
      routeParams: {
        conversationId,
        key: 'description',
      },
    })
  }, [conversationId, handleNavigation])

  const onUploadIcon = useCallback(
    async (icon: MediaFile) => {
      await updateConversationMutation.mutateAsync({
        icon,
      })
    },
    [updateConversationMutation]
  )

  const onFavorite = useCallback(
    async (favorite: boolean) => {
      await updateConversationMutation.mutateAsync({
        changes: {
          favorite,
        },
      })
    },
    [updateConversationMutation]
  )

  const onMute = useCallback(
    async (muted: boolean) => {
      await updateConversationMutation.mutateAsync({
        changes: {
          muted,
        },
      })
    },
    [updateConversationMutation]
  )

  const onToggleManagerReadOnly = useCallback(
    async (managerReadOnly: boolean) => {
      await updateConversationMutation.mutateAsync({
        changes: {
          managerReadOnly,
        },
      })
    },
    [updateConversationMutation]
  )

  const onToggleCrewReadOnly = useCallback(
    async (crewReadOnly: boolean) => {
      await updateConversationMutation.mutateAsync({
        changes: {
          crewReadOnly,
        },
      })
    },
    [updateConversationMutation]
  )

  const onToggleBirthdayWorkAnniversaryAndNewHireReminders = useCallback(
    async (birthdayWorkAnniversaryAndNewHireRemindersEnabled: boolean) => {
      await updateConversationMutation.mutateAsync({
        changes: {
          birthdayWorkAnniversaryAndNewHireRemindersEnabled,
        },
      })
    },
    [updateConversationMutation]
  )

  const onDelete = useCallback(async () => {
    await deleteConversationMutation.mutateAsync()
    handleNavigation({
      routeName: 'conversations',
      routeParams: {
        screen:
          user?.role === 'observer' || user?.role === 'admin'
            ? 'groups'
            : 'allConversations',
      },
    })
  }, [deleteConversationMutation, handleNavigation, user?.role])

  const onLeave = useCallback(async () => {
    await leaveConversationMutation.mutateAsync()
    handleNavigation({
      routeName: 'conversations',
      routeParams: {
        screen:
          user?.role === 'observer' || user?.role === 'admin'
            ? 'groups'
            : 'allConversations',
      },
    })
  }, [handleNavigation, leaveConversationMutation, user?.role])

  const onPressViewAllMembers = useCallback(
    (search?: boolean) => {
      handleNavigation({
        routeName: 'chat_group_members',
        routeParams: {
          conversationId,
          search,
        },
      })
    },
    [conversationId, handleNavigation]
  )

  return (
    <StatusBoundary
      loadingMessage={
        deleteConversationMutation.isPending ? t('deleting') : undefined
      }
      isLoading={
        conversationQuery.isLoading || deleteConversationMutation.isPending
      }
      error={
        conversationQuery.error ??
        deleteConversationMutation.error ??
        updateConversationMutation.error ??
        updateMembersMutation.error ??
        membersQuery.error
      }
    >
      <ConversationDetailsView
        conversation={conversation}
        nonAliasMembers={shouldLoadMembers ? nonAliasMembers : null}
        memberAliases={memberAliases}
        totalMemberCount={allMembersQuery.data?.length}
        recentFiles={recentFilesQuery.data}
        recentFilesLoading={recentFilesQuery.isLoading}
        isMembersLoading={membersQuery.isLoading || allMembersQuery.isFetching}
        user={user}
        onAddMembers={onAddMembers}
        onRemoveMember={onRemoveMember}
        onSendDirectMessage={onSendDirectMessage}
        onToggleAdminStatus={onToggleAdminStatus}
        onDelete={onDelete}
        onLeave={onLeave}
        onMute={onMute}
        onFavorite={onFavorite}
        onEditName={onEditName}
        onEditDescription={onEditDescription}
        onUploadIcon={onUploadIcon}
        onToggleManagerReadOnly={onToggleManagerReadOnly}
        onToggleCrewReadOnly={onToggleCrewReadOnly}
        onToggleBirthdayWorkAnniversaryAndNewHireReminders={
          onToggleBirthdayWorkAnniversaryAndNewHireReminders
        }
        onSeeAllFiles={onSeeAllFiles}
        onRecentDocumentPress={onPressFile}
        onRecentDocumentLongPress={onLongPressResource}
        onPressViewAllMembers={onPressViewAllMembers}
        onAliasRemoved={onAliasRemoved}
      />
    </StatusBoundary>
  )
}
